import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "@/components/Reveal";
import { LEGAL_PAGES } from "@/data/legal";
import { SITE } from "@/data/site";

export default function Legal() {
    const { slug } = useParams();
    const doc = LEGAL_PAGES.find((p) => p.slug === slug);
    if (!doc) return <Navigate to="/" replace />;

    return (
        <>
            <section data-testid={`legal-hero-${doc.slug}`} className="relative overflow-hidden">
                <div className="pointer-events-none absolute -top-40 right-[-10%] h-[26rem] w-[26rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="mx-auto max-w-4xl px-6 pt-40 pb-14 md:px-10 md:pt-48 md:pb-16">
                    <MaskedLine delay={0.05}>
                        <Eyebrow>{doc.eyebrow}</Eyebrow>
                    </MaskedLine>
                    <h1 className="mt-8 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                        <MaskedLine delay={0.2}>{doc.title}</MaskedLine>
                    </h1>
                    <Reveal delay={0.45} className="mt-6">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500" data-testid="legal-updated">
                            {doc.updated}
                        </p>
                        <p className="mt-6 max-w-2xl font-editorial italic font-light text-xl text-zinc-400">
                            {doc.intro}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-6 pb-24 md:px-10 md:pb-32">
                <div className="border-t border-white/10">
                    {doc.sections.map((s) => (
                        <Reveal key={s.h}>
                            <div className="border-b border-white/10 py-10 md:py-12" data-testid={`legal-section-${s.h.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                                <h2 className="font-display text-xl md:text-2xl font-extrabold tracking-tight">{s.h}</h2>
                                <div className="mt-5 space-y-4">
                                    {s.ps.map((p) => (
                                        <p key={p.slice(0, 40)} className="text-sm md:text-base leading-relaxed text-zinc-400">
                                            {p}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-14 flex flex-wrap items-center justify-between gap-8">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Questions about this document?</p>
                        <a
                            href={`mailto:${SITE.email}`}
                            data-testid="legal-email-link"
                            className="mt-3 inline-flex items-center gap-2 text-sm text-gold transition-colors duration-300 hover:text-gold-light"
                        >
                            {SITE.email} <ArrowUpRight size={14} />
                        </a>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {LEGAL_PAGES.filter((p) => p.slug !== doc.slug).map((p) => (
                            <Link
                                key={p.slug}
                                to={`/legal/${p.slug}`}
                                data-testid={`legal-nav-${p.slug}`}
                                className="rounded-full border border-white/15 px-6 py-2.5 text-sm text-zinc-300 transition-colors duration-300 hover:border-gold/50 hover:text-gold"
                            >
                                {p.title}
                            </Link>
                        ))}
                    </div>
                </Reveal>
            </section>
        </>
    );
}
